import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getAuthHeaders } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Film, Users, Loader2, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE } from '../../config/api';
import { loadRazorpayScript } from '../../lib/razorpay';

const API = API_BASE;

const ADDON_ICONS = { extra_production: Film, extra_seat: Users };

// Extra production / extra seat add-on subscriptions (Phase 2 Task 2)
export const AddonsCard = ({ workspaceId }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null); // addon_type currently checking out

  const fetchAddons = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/workspaces/${workspaceId}/addons`, {
        headers: getAuthHeaders(),
      });
      setData(res.data);
    } catch (e) {
      console.error('Failed to load add-ons:', e);
    }
  }, [workspaceId]);

  useEffect(() => {
    if (workspaceId) fetchAddons();
  }, [workspaceId, fetchAddons]);

  if (!data || data.plan_tier === 'free') return null;

  const handleSubscribe = async (addonType) => {
    setBusy(addonType);
    try {
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        toast.error('Failed to load payment gateway');
        setBusy(null);
        return;
      }
      const subRes = await axios.post(
        `${API}/workspaces/${workspaceId}/addons/${addonType}/subscribe`,
        {},
        { headers: getAuthHeaders() }
      );
      const sub = subRes.data;
      const rzp = new window.Razorpay({
        key: sub.key_id,
        subscription_id: sub.subscription_id,
        name: 'FrameFlow',
        description: `${sub.display_name} — ${sub.amount_display}`,
        handler: async (response) => {
          try {
            const verifyRes = await axios.post(
              `${API}/workspaces/${workspaceId}/addons/verify`,
              {
                razorpay_subscription_id: response.razorpay_subscription_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              },
              { headers: getAuthHeaders() }
            );
            if (verifyRes.data.status === 'success') {
              toast.success(`${sub.display_name} added — your limit is raised immediately`);
              await fetchAddons();
            }
          } catch (e) {
            toast.error('Payment verification failed — if you were charged, the add-on will activate automatically.');
          }
          setBusy(null);
        },
        modal: { ondismiss: () => setBusy(null) },
        theme: { color: '#F59E0B' },
      });
      rzp.open();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to start add-on purchase');
      setBusy(null);
    }
  };

  const handleCancel = async (addon) => {
    if (!window.confirm(`Cancel this ${data.catalog[addon.addon_type]?.display_name || 'add-on'}? Your limit reduces, but nothing gets archived.`)) return;
    try {
      await axios.post(
        `${API}/workspaces/${workspaceId}/addons/${addon.razorpay_subscription_id}/cancel`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success('Add-on cancelled');
      await fetchAddons();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to cancel add-on');
    }
  };

  const activeAddons = (data.addons || []).filter((a) => a.status === 'active');

  return (
    <Card className="p-4" data-testid="addons-card">
      <p className="font-semibold mb-1">Plan add-ons</p>
      <p className="text-sm text-muted-foreground mb-4">
        Hit a limit? Add capacity without changing plans.
      </p>

      <div className="space-y-3">
        {Object.entries(data.catalog).map(([addonType, entry]) => {
          const Icon = ADDON_ICONS[addonType] || Plus;
          const count = data.counts?.[addonType] || 0;
          return (
            <div key={addonType} className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3 min-w-0">
                <div className="p-2 rounded-lg bg-muted">
                  <Icon className="h-4 w-4 text-muted-foreground" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium">
                    {entry.display_name}
                    {count > 0 && (
                      <span className="ml-2 text-xs px-1.5 py-0.5 rounded-full bg-primary/10 text-primary font-semibold">
                        {count} active
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-muted-foreground">₹{Math.round(entry.price_inr)}/month each</p>
                </div>
              </div>
              {data.is_owner && entry.configured && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleSubscribe(addonType)}
                  disabled={busy !== null}
                  data-testid={`addon-${addonType.replace(/_/g, '-')}-add-btn`}
                >
                  {busy === addonType ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <><Plus className="h-4 w-4 mr-1" />Add</>
                  )}
                </Button>
              )}
            </div>
          );
        })}
      </div>

      {data.is_owner && activeAddons.length > 0 && (
        <div className="mt-4 pt-3 border-t border-border space-y-2">
          {activeAddons.map((addon) => (
            <div key={addon.id} className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                {data.catalog[addon.addon_type]?.display_name || addon.addon_type}
                {' · '}active since {addon.activated_at ? new Date(addon.activated_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '—'}
              </span>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 text-destructive hover:text-destructive"
                onClick={() => handleCancel(addon)}
                data-testid={`addon-cancel-${addon.id}`}
              >
                <X className="h-3.5 w-3.5 mr-1" />Cancel
              </Button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

export default AddonsCard;
